import React, { useEffect, useRef, useState } from 'react'
import { Button, Checkbox, notification, Table, Typography } from 'antd'
import { ColumnProps } from 'antd/lib/table'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'

import {
  Id,
  PolicyFlowApproval,
  PolicyFlowFormat,
  PolicyFlowType
} from 'src/types'
import { Tooltip } from 'src/components/Ant'
import * as PoliciesService from 'src/services/api/policies-flow'

import * as CSS from './styles'
import * as Values from './initial-values'
import FlowApprovalsModal from './flow-approvals-modal'
import sanitize from './sanitize'
import validationSchema from './validation-schema'

type PolicyFlowApprovalList = PolicyFlowApproval & { key: Id }

export default function ApprovalFlow() {
  const [dataSource, setDataSource] = useState<PolicyFlowApprovalList[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [modalPolicyFlowFormat, setModalPolicyFlowFormat] =
    useState<PolicyFlowFormat>(PolicyFlowFormat.STANDARD)
  const [modalPolicyFlowType, setModalPolicyFlowType] =
    useState<PolicyFlowType>(PolicyFlowType.HIERARCHY)

  const refFormik = useRef<Formik<PolicyFlowApproval>>(null)
  const isSubscribed = useRef(true)
  const { t } = useTranslation()

  useEffect(
    () => () => {
      isSubscribed.current = false
    },
    []
  )

  useEffect(() => {
    setIsLoading(true)
    PoliciesService.getPolicyApprovals()
      .then(response => {
        if (isSubscribed.current) {
          if (response.status === 200) {
            const approvals: PolicyFlowApprovalList[] =
              response.data?.policy_approvals.map(data => ({
                ...data,
                key: data.policy_approval_id
              })) || []
            setDataSource(approvals)
          } else {
            notification.error({
              message: t('common.response.status-500'),
              placement: 'bottomRight'
            })
          }
        }
      })
      .catch(response => {
        notification.error({
          message: t('common.response.status-500'),
          placement: 'bottomRight'
        })
        //Send Bugsnag?
      })
      .finally(() => {
        if (isSubscribed.current) {
          setIsLoading(false)
        }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const columns: ColumnProps<PolicyFlowApprovalList>[] = [
    {
      align: 'center',
      dataIndex: 'code',
      render: (code: string) => {
        if (code?.length > 50) {
          return <Tooltip title={code}>{`${code.substring(0, 47)}...`}</Tooltip>
        }
        return <Typography.Text>{code}</Typography.Text>
      },
      title: (
        <Typography.Text strong>
          {t('web.resources.common.code.title')}
        </Typography.Text>
      ),
      width: 120
    },
    {
      align: 'center',
      dataIndex: 'name',
      render: (name: string) => {
        if (name?.length > 50) {
          return <Tooltip title={name}>{`${name.substring(0, 47)}...`}</Tooltip>
        }
        return <Typography.Text>{name}</Typography.Text>
      },
      title: (
        <Typography.Text strong>
          {t('web.resources.common.name.title')}
        </Typography.Text>
      )
    },
    {
      align: 'center',
      dataIndex: '',
      render: (record: PolicyFlowApproval) => {
        const { cost_center_approval, reason_approval } = record

        if (cost_center_approval) {
          return (
            <Typography.Text>
              {t('web.assignment.sharing.form.cost-center.title')}
            </Typography.Text>
          )
        }

        if (reason_approval) {
          return (
            <Typography.Text>
              {t('web.policies-flow.type.reason')}
            </Typography.Text>
          )
        }

        if (!cost_center_approval && !reason_approval) {
          return (
            <Typography.Text>
              {t('web.policies-flow.type.hierarchy')}
            </Typography.Text>
          )
        }

        return ''
      },
      title: (
        <Typography.Text strong>
          {t('web.policies-flow.approval.type.title')}
        </Typography.Text>
      )
    },
    {
      align: 'center',
      dataIndex: '',
      render: (record: PolicyFlowApproval) => {
        const { automatic_approval, consensual_approval } = record

        if (automatic_approval) {
          return (
            <Typography.Text>
              {t('web.policies-flow.format.automatic')}
            </Typography.Text>
          )
        }

        if (consensual_approval) {
          return (
            <Typography.Text>
              {t('web.policies-flow.format.consensus')}
            </Typography.Text>
          )
        }

        if (!automatic_approval && !consensual_approval) {
          return (
            <Typography.Text>
              {t('web.policies-flow.format.standard')}
            </Typography.Text>
          )
        }

        return ''
      },
      title: (
        <Typography.Text strong>
          {t('web.policies-flow.approval.format.title')}
        </Typography.Text>
      )
    },
    {
      align: 'center',
      dataIndex: 'exception_approver',
      render: (value: boolean) => <Checkbox checked={value} disabled />,
      title: (
        <Typography.Text strong>
          {t('web.policies-flow.exception-modal.title')}
        </Typography.Text>
      ),
      width: 120
    },
    {
      align: 'center',
      dataIndex: '',
      render: record => (
        <CSS.Switch
          style={{ width: '100%' }}
          checked={record.active}
          onClick={() => {
            setIsLoading(true)
            PoliciesService.SavePolicyApprovals({
              ...record,
              active: !record.active
            })
              .then(response => {
                if (isSubscribed.current) {
                  if (response.status === 200) {
                    const record = response.data.policy_approvals[0]
                    setDataSource(data =>
                      data.map(d =>
                        d.policy_approval_id === record.policy_approval_id
                          ? { ...record, key: record.policy_approval_id }
                          : d
                      )
                    )
                  } else {
                    notification.error({
                      message: t('common.response.status-500'),
                      placement: 'bottomRight'
                    })
                  }
                }
              })
              .catch(response => {
                notification.error({
                  message: t('common.response.status-500'),
                  placement: 'bottomRight'
                })
              })
              .finally(() => {
                if (isSubscribed.current) {
                  setIsLoading(false)
                }
              })
          }}
        />
      ),
      title: <Typography.Text strong>{t('common.active')}</Typography.Text>,
      width: 100
    },
    {
      align: 'center',
      dataIndex: '',
      render: (record: PolicyFlowApproval) => (
        <Button
          icon="edit"
          type="ghost"
          onClick={() => {
            refFormik.current?.resetForm(record)

            let type = PolicyFlowType.HIERARCHY
            let format = PolicyFlowFormat.STANDARD

            if (record.automatic_approval) {
              format = PolicyFlowFormat.AUTOMATIC
            }
            if (record.consensual_approval) {
              format = PolicyFlowFormat.CONSENSUS
            }

            if (record.cost_center_approval) {
              type = PolicyFlowType.COST_CENTER
            }
            if (record.reason_approval) {
              type = PolicyFlowType.REASON
            }

            setModalPolicyFlowFormat(format)
            setModalPolicyFlowType(type)
          }}
        />
      ),
      title: (
        <Typography.Text strong>{t('common.actions.edit')}</Typography.Text>
      ),
      width: 120
    }
  ]

  function handleSubmit(values: PolicyFlowApproval) {
    setIsLoading(true)
    const sanitized = sanitize(values)
    PoliciesService.SavePolicyApprovals(sanitized)
      .then(response => {
        if (isSubscribed.current) {
          if (response.status === 200) {
            const record = response.data.policy_approvals[0]
            if (typeof values.policy_approval_id === 'string') {
              //is new
              setDataSource(data => [
                ...data,
                { ...record, key: record.policy_approval_id }
              ])
            } else {
              //is an update
              setDataSource(data =>
                data.map(d =>
                  d.policy_approval_id === record.policy_approval_id
                    ? { ...record, key: record.policy_approval_id }
                    : d
                )
              )
            }
            refFormik.current.resetForm(Values.initialValues)
          } else {
            notification.error({
              message: t('common.response.status-500'),
              placement: 'bottomRight'
            })
          }
        }
      })
      .catch(response => {
        notification.error({
          message: t('common.response.status-500'),
          placement: 'bottomRight'
        })
      })
      .finally(() => {
        if (isSubscribed.current) {
          setIsLoading(false)
        }
      })
  }

  function ButtonAdd() {
    return (
      <Button
        icon="plus"
        type="primary"
        onClick={() => {
          if (refFormik.current) {
            refFormik.current.resetForm(Values.defaultValue)
            setModalPolicyFlowFormat(PolicyFlowFormat.STANDARD)
            setModalPolicyFlowType(PolicyFlowType.HIERARCHY)
          }
        }}
      >
        {t('common.actions.add')}
      </Button>
    )
  }

  return (
    <>
      <CSS.ListTitle>
        <span>{t('web.policies-flow.list-title')}</span>
        <ButtonAdd />
      </CSS.ListTitle>
      <Table
        className="table-striped"
        columns={columns}
        dataSource={dataSource}
        loading={isLoading}
        locale={{
          emptyText: t('common.no-data-found')
        }}
        pagination={false}
      />
      <br />
      <CSS.ButtonContainer>
        <ButtonAdd />
      </CSS.ButtonContainer>
      <Formik
        initialValues={Values.initialValues}
        onSubmit={handleSubmit}
        ref={refFormik}
        render={formikProps => {
          if (!formikProps.values.policy_approval_id) {
            return <span />
          }

          const isVisible = Boolean(formikProps?.values?.policy_approval_id)

          return (
            <FlowApprovalsModal
              formikProps={formikProps}
              isLoading={isLoading}
              isVisible={isVisible}
              modalPolicyFlowFormat={modalPolicyFlowFormat}
              modalPolicyFlowType={modalPolicyFlowType}
              refFormik={refFormik}
              setModalPolicyFlowFormat={setModalPolicyFlowFormat}
              setModalPolicyFlowType={setModalPolicyFlowType}
            />
          )
        }}
        validationSchema={validationSchema}
      />
    </>
  )
}
